import { Image, StyleSheet, Text, View, Link } from "@react-pdf/renderer";
import React, { isValidElement, ReactChild } from "react";
import { parserTags } from "./parserTags";

const styles = StyleSheet.create({
  h1: {
    marginBottom: '14px',
    marginTop: '10px',
    fontSize: '32px',
    fontWeight: 700,
    fontFamily: "Manrope",
  },
  h2: {
    marginBottom: '14px',
    marginTop: '10px',
    fontSize: '30px',
    fontWeight: 700,
    fontFamily: "Manrope",
  },
  h3: {
    fontSize: '18.72px',
    marginBottom: '20px',
    marginTop: '15px',
    fontWeight: 700,
    fontFamily: "Manrope",
  },
  h4: {
    fontSize: '14.5px',
    marginBottom: '13px',
    fontWeight: 700,
    fontFamily: "Manrope",
  },
  h5: {
    fontSize: '13.28px',
    marginBottom: '14px',
    fontWeight: 700,
    fontFamily: "Manrope",
  },
  p: {
    fontSize: '12px',
    lineHeight: '1.6px',
    fontWeight: "normal",
    fontFamily: "Manrope",
  },
  em: {
    fontStyle: 'italic',
    color: 'blue'
  },
  ul: {
    flexDirection: "column",
    position: "relative"
  },
  ol: {
    flexDirection: "column",
    margin: '10px 0'
  },
  li: {
    fontSize: '13px',
    flexDirection: "row",
    paddingBottom: '6px',
    maxWidth: '360px',
    fontFamily: "Manrope",
  },
  strong: {
    fontWeight: 700,
    margin: '20px 0',
    fontSize: '13px',
    fontFamily: "Manrope",
  },
  blockquote: {
    position: 'relative',
    borderLeft: '1.5px solid #19abff',
    borderTop: '1.5px solid #19abff',
    borderRight: '1.5px solid #19abff',
    borderBottom: '4px solid #19abff',
    borderRadius: '7px',
    paddingRight: '40px',
    paddingLeft: '35px',
    paddingTop: '15px',
    paddingBottom: '15px',
    marginTop: '20px',
    marginBottom: '10px'
  },
  infoIcon: {
    position: 'absolute',
    backgroundColor: '#19abff',
    top: '15px',
    left: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '18px',
    width: '18px',
    borderRadius: '500px'
  },
  link: {
    color: '#0081cc',
    fontSize: '12px',
    lineHeight: '1.5px',
    fontWeight: "normal",
    fontFamily: "Manrope",
  },
  table: {
    width: '100%',
    fontSize: '12px',
    border: '1px solid #EEE',
    borderRadius: '5px',
    marginBottom: '15px'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: '1px solid #EEE',
    paddingTop: 8,
    paddingBottom: 8,
  },
  header: {
    borderTop: 'none',
  },
  bold: {
    fontWeight: 'bold',
  },
  row1: {
    width: '22%',
    paddingLeft: '7px'
  },
  column: {
    flexDirection: "column",
    fontFamily: "Manrope",
  },
  pre: {
    fontSize: '14px',
    fontFamily: "Manrope",
  },
  img: {
    margin: '14px 0',
    maxHeight: '500px',
    maxWidth: '500px',
    borderRadius: '5px'
  }
});

export const h1 = (child: ReactChild) => {
  return (<Text style={styles.h1}>{child}</Text>)
}

export const h2 = (child: ReactChild) => {
  return (<Text style={styles.h2}>{child}</Text>)
}

export const h3 = (child: ReactChild) => {
  return (<Text style={styles.h3}>{child}</Text>)
}

export const h4 = (child: ReactChild) => {
  return (<Text style={styles.h4}>{child}</Text>)
}

export const h5 = (child: ReactChild) => {
  return (<Text style={styles.h5}>{child}</Text>)
}

export const link = (child: ReactChild) => {
  let src = String(child)
  if (src.includes('@')) {
    src = `mailto: ${child}`
  }
  return (<Link style={styles.link} src={src}>{child}</Link>)
}

export const strong = (child: ReactChild[]) => {
  return child.map((i, index) => {
    return (<Text key={index} style={styles.strong}>{i}</Text>)
  })
}

export const img = (src: string) => {
  const forbiddenImg: Array<string> = [
    'szkYQ9IF5do',
    'YVqDS0QDUUk',
    'SUo9lmPxIqA',
    '86rvNPX00Fk',
    'att_1_for_528482347',
    '525172899'
  ]

  const imgSizes: any = {
    'mPkmELd31HE': {w: '200px', h: '200px'},
    'KGDkrDwhlLU': {w: '445px', h: '74px'},
    'EFhILHSDZgI': {w: '34px', h: '35px'},
    'rNS5CPGiDXw': {w: '142px', h: '49px'},
    'qdz79YgNiqM': {w: '307px', h: '98px'},
    'f9M62Cwwecw': {w: '342px', h: '119px'}
  }

  const foundForbiddenImg = forbiddenImg
    .find((img) => src.includes(img))

  if (foundForbiddenImg) {
    return (<View/>)
  }

  const foundKey = Object.keys(imgSizes)
    .find((img) => src.includes(img))

  if (foundKey) {
    return <Image
      src={src}
      style={{
        height: imgSizes[foundKey].h,
        width: imgSizes[foundKey].w
      }}
    />
  }
  return (<Image style={styles.img} src={src} />)
}

export const paragraph = (child: ReactChild[]) => {
  const mappedContent = child.map((c, index) => {
    if (isValidElement(c)) {
      return parserTags(c)
    }
    return (<Text key={index}>{c}</Text>)
  })

  let block = (
    <View style={styles.p}>
      <Text>
        {mappedContent}
      </Text>
    </View>

  )
  const isSomeMappedContent = mappedContent
    .some((i) =>
      ("type" in i && i.type === "IMAGE")
  )

  if (isSomeMappedContent) {
    block = (<View>{mappedContent}</View>)
  }

  return block
}

export const ul = (child: ReactChild[]) => {
  return (
    <View>
      {child.map((c, index) => {
        return (
          <View style={styles.ul} key={index}>
            {isValidElement(c) ? parserTags(c) : c}
          </View>
        )
      })}
    </View>
  )
}

export const li = (child: ReactChild[]) => {
  const mappedContent = child.map((i, index) => {
    let children = []
    if (isValidElement(i)) {
      children.push(parserTags(i))
    } else {
      children.push((<Text>{i}</Text>))
    }

    return (
      <View style={[styles.li, {paddingLeft: '10px'}]} key={index}>
        {children}
      </View>
    )
  })
  return (
    <View style={{position: "relative"}}>
      <Text style={{position: 'absolute', top: '0'}}>•</Text>
      {mappedContent}
    </View>
  )
}

export const blockquote = (child: ReactChild[]) => {
  const mappedContent = child.map((i) => {
    if (isValidElement(i)) {
      return parserTags(i)
    }
    return (<Text style={styles.p}>{i}</Text>)
  })
  return (
    <View style={styles.blockquote}>
      <View style={styles.infoIcon}>
        <Text style={{color: 'white', fontSize: '13px'}}>i</Text>
      </View>
      {mappedContent}
    </View>
  )
}

export const ol = (content: { children: Array<string | object>, start?: string }) => {
  let startIndex = content.start ? Number(content.start) : 1
  return (
    <View style={styles.ol}>
      {content.children.map((c: any, indexChildren: number) => {
        return (
          <View style={styles.li} key={indexChildren}>
            <Text>{startIndex + indexChildren}.</Text>
            <Text style={{paddingLeft: '3px'}}>{c}</Text>
          </View>
        )
      })}
    </View>
  )
}

export const table = (child: ReactChild[]) => {
  return (
    <View style={styles.table}>
      {child.map((i) => (
        isValidElement(i) ? parserTags(i) : i
      ))}
    </View>
  )
}

export const thead = (child: ReactChild[]) => {
  return (
    <View>
      {/*// in content we have empty field*/}
      {/*{child.map((i) => parserTags(i))}*/}
    </View>
  )
}

export const tbody = (child: ReactChild[]) => {
  return (
    <View>
      {child.map((i) => (
        isValidElement(i) ? parserTags(i) : i
      ))}
    </View>)
}

export const tr = (child: ReactChild[]) => {
  return (
    <View style={styles.row}>
      {child.map((i) => (
        isValidElement(i) ? parserTags(i) : i
      ))}
    </View>
  )
}

export const td = (child: ReactChild[]) => {
  const mappedContent = child.map((i, index) => {
    if (isValidElement(i)) {
      return parserTags(i)
    }
    return (
      <View style={styles.column} key={index}>
        <Text style={{paddingTop: '5px', width: '100%'}}>{i}</Text>
      </View>
    )
  })

  return (
    <View style={styles.row1}>
      {mappedContent}
    </View>
  )
}

export const th = (child: ReactChild) => {
  return (
    <View style={styles.row1}></View>
  )
}

export const em = (child: ReactChild) => {
  return (<Text style={styles.em}>{child}</Text>)
}

export const span = (child: ReactChild[]) => {
  const mappedContent = child.map((i, index) => {
    if (isValidElement(i)) {
      return parserTags(i)
    }
    return <Text key={index}>{i}</Text>
  })
  return <View>{mappedContent}</View>
}

export const pre = (child: ReactChild) => {
  return (
    <View style={styles.pre}>
      <Text>{child}</Text>
    </View>
  )
}